// =============================================================================
// Template Editor Modal - 模板编辑/创建模态框
// =============================================================================

import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, AlertCircle, CheckCircle } from 'lucide-react';
import { apiClient } from '../services/apiClient';
import LoadingSpinner from './LoadingSpinner';

const TemplateEditorModal = ({ isOpen, onClose, template, onSuccess }) => {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    category: 'Backend',
    languages: [],
    tools: [],
    docker: {
      image: '',
      cmd: ['/bin/sh'],
      workdir: '/workspace'
    },
    ports: [],
    environment: {},
    resources: {
      memory: '1g',
      cpu: '1'
    }
  });
  const [validation, setValidation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [newTool, setNewTool] = useState('');
  const [newPortName, setNewPortName] = useState('');
  const [newPortContainer, setNewPortContainer] = useState('');
  const [newEnvKey, setNewEnvKey] = useState('');
  const [newEnvValue, setNewEnvValue] = useState('');

  const isEditing = !!template;

  useEffect(() => {
    if (isOpen) {
      if (template) {
        setFormData({
          ...template,
          docker: template.docker || { image: '', cmd: ['/bin/sh'], workdir: '/workspace' },
          ports: template.ports || [],
          environment: template.environment || {},
          resources: template.resources || { memory: '1g', cpu: '1' }
        });
      } else {
        setFormData({
          id: '',
          name: '',
          description: '',
          category: 'Backend',
          languages: [],
          tools: [],
          docker: {
            image: '',
            cmd: ['/bin/sh'],
            workdir: '/workspace'
          },
          ports: [],
          environment: {},
          resources: {
            memory: '1g',
            cpu: '1'
          }
        });
      }
      setValidation(null);
      setError('');
    }
  }, [isOpen, template]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const addLanguage = () => {
    if (newLanguage && !formData.languages.includes(newLanguage)) {
      setFormData(prev => ({
        ...prev,
        languages: [...prev.languages, newLanguage]
      }));
      setNewLanguage('');
    }
  };

  const removeLanguage = (lang) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.filter(l => l !== lang)
    }));
  };

  const addTool = () => {
    if (newTool && !formData.tools.includes(newTool)) {
      setFormData(prev => ({
        ...prev,
        tools: [...prev.tools, newTool]
      }));
      setNewTool('');
    }
  };

  const removeTool = (tool) => {
    setFormData(prev => ({
      ...prev,
      tools: prev.tools.filter(t => t !== tool)
    }));
  };

  const addPort = () => {
    if (newPortName && newPortContainer) {
      setFormData(prev => ({
        ...prev,
        ports: [...prev.ports, {
          name: newPortName,
          container: parseInt(newPortContainer),
          host: 0
        }]
      }));
      setNewPortName('');
      setNewPortContainer('');
    }
  };

  const removePort = (index) => {
    setFormData(prev => ({
      ...prev,
      ports: prev.ports.filter((_, i) => i !== index)
    }));
  };

  const addEnvironment = () => {
    if (newEnvKey && newEnvValue) {
      setFormData(prev => ({
        ...prev,
        environment: {
          ...prev.environment,
          [newEnvKey]: newEnvValue
        }
      }));
      setNewEnvKey('');
      setNewEnvValue('');
    }
  };

  const removeEnvironment = (key) => {
    setFormData(prev => ({
      ...prev,
      environment: Object.fromEntries(
        Object.entries(prev.environment).filter(([k]) => k !== key)
      )
    }));
  };

  const validateTemplate = async () => {
    try {
      const response = await apiClient.post('/admin/templates/validate', formData);
      if (response.data.success) {
        setValidation(response.data.data);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (isEditing) {
        await apiClient.put(`/admin/templates/${template.id}`, formData);
      } else {
        await apiClient.post('/admin/templates', formData);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to save template:', error);
      setError(error.response?.data?.error || 'Failed to save template');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {isEditing ? '编辑模板' : '创建模板'}
          </h2>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 bg-white dark:bg-gray-800">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">模板ID *</label>
              <input
                type="text"
                name="id"
                value={formData.id}
                onChange={handleInputChange}
                disabled={isEditing}
                placeholder="nodejs"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">模板名称 *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Node.js"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">描述 *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Node.js development environment with npm and common tools"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500 dark:placeholder-gray-400"
              rows="3"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">分类 *</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="Backend">Backend</option>
              <option value="Frontend">Frontend</option>
              <option value="Database">Database</option>
              <option value="DevOps">DevOps</option>
              <option value="AI/ML">AI/ML</option>
              <option value="Mobile">Mobile</option>
            </select>
          </div>

          {/* Languages */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">支持的语言</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.languages.map((lang) => (
                <span key={lang} className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 rounded text-sm">
                  {lang}
                  <button type="button" onClick={() => removeLanguage(lang)}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newLanguage}
                onChange={(e) => setNewLanguage(e.target.value)}
                placeholder="添加语言"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500 dark:placeholder-gray-400"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLanguage())}
              />
              <button type="button" onClick={addLanguage} className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Tools */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">预装工具</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.tools.map((tool) => (
                <span key={tool} className="flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 rounded text-sm">
                  {tool}
                  <button type="button" onClick={() => removeTool(tool)}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTool}
                onChange={(e) => setNewTool(e.target.value)}
                placeholder="添加工具"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500 dark:placeholder-gray-400"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTool())}
              />
              <button type="button" onClick={addTool} className="px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Docker Configuration */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">Docker 配置</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Docker 镜像 *</label>
                <input
                  type="text"
                  name="docker.image"
                  value={formData.docker.image}
                  onChange={handleInputChange}
                  placeholder="node:18-alpine"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500 dark:placeholder-gray-400"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">工作目录</label>
                <input
                  type="text"
                  name="docker.workdir"
                  value={formData.docker.workdir}
                  onChange={handleInputChange}
                  placeholder="/workspace"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>
            </div>
          </div>

          {/* Validation */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={validateTemplate}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              验证配置
            </button>
            {validation && (
              <div className={`flex items-center gap-2 px-3 py-2 rounded-md ${
                validation.valid
                  ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300'
                  : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300'
              }`}>
                {validation.valid ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                <span className="text-sm">
                  {validation.valid ? '配置有效' : `${validation.errors.length} 个错误`}
                </span>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
              <div className="flex items-center gap-2 text-red-800 dark:text-red-300">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading && <LoadingSpinner size="sm" />}
              {isEditing ? '更新模板' : '创建模板'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplateEditorModal;
